import React from 'react';
import {
  Card,
  CardBody,
  Typography,
  Checkbox,
  Chip,
} from '@material-tailwind/react';
import { AdjustmentsHorizontalIcon, CurrencyDollarIcon, SparklesIcon, QueueListIcon } from '@heroicons/react/24/outline';

// Define section order and labels
const SECTION_ORDER = [
  'cover',
  'tableOfContents',
  'about',
  'marketTrends',
  'onMarketComps',
  'recentlySoldComps',
  'summaryPricing',
  'strategy',
  'assumptions',
];

const SECTION_LABELS = {
  cover: 'Cover Page',
  tableOfContents: 'Table of Contents',
  about: 'About',
  marketTrends: 'Market Trends',
  onMarketComps: 'Market comps.',
  recentlySoldComps: 'Sold comps.',
  summaryPricing: 'Summary & Pricing',
  strategy: 'Brokerage Services',
  assumptions: 'Thank You',
};

// Sort sections by defined order
function sortSections(sections) {
  return [...sections].sort((a, b) => {
    const indexA = SECTION_ORDER.indexOf(a.id);
    const indexB = SECTION_ORDER.indexOf(b.id);
    // Put unknown sections at the end
    const orderA = indexA === -1 ? SECTION_ORDER.length : indexA;
    const orderB = indexB === -1 ? SECTION_ORDER.length : indexB;
    return orderA - orderB;
  });
}

function SectionList({
  sections,
  selectedSection,
  onSelectSection,
  onToggleSection,
  hasDraftChanges,
  hasCompChanges,
  hasGuidelinesChanges,
  hasExclusionChanges,
  pendingExclusionsCount,
  hasSalesCompExclusionChanges,
  isLocked,
  periodDays,
}) {
  const isDraftSettingsSelected = selectedSection === 'draftSettings';
  const isAiSettingsSelected = selectedSection === 'aiSettings';
  const isAiCostsSelected = selectedSection === 'aiCosts';
  const isListingsSelected = selectedSection === 'listings';

  const listingsTitle = periodDays ? `Listings (${periodDays} days)` : 'Listings';

  return (
    <Card className={`h-full flex flex-col ${isLocked ? 'opacity-50 pointer-events-none' : ''}`}>
      <CardBody className="p-4 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <Typography variant="h6" color="blue-gray">
            Report Sections
          </Typography>
          {isLocked && (
            <Chip size="sm" value="Refreshing..." color="blue" variant="ghost" />
          )}
        </div>
        <div className="flex flex-col gap-2 overflow-y-auto flex-1">
          {/* Draft Settings - special item at top */}
          <div
            className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
              isDraftSettingsSelected
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => onSelectSection('draftSettings')}
          >
            <div className="flex items-center gap-3">
              <AdjustmentsHorizontalIcon className="h-5 w-5 text-blue-500" />
              <Typography variant="small" className="font-medium">
                Draft Settings
              </Typography>
            </div>
            <div className="flex flex-wrap gap-1">
              {hasCompChanges && (
                <Chip
                  size="sm"
                  value="Comps"
                  color="green"
                  variant="ghost"
                />
              )}
              {hasDraftChanges && (
                <Chip
                  size="sm"
                  value="Modified"
                  color="amber"
                  variant="ghost"
                />
              )}
            </div>
          </div>

          {/* AI Settings */}
          <div
            className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
              isAiSettingsSelected
                ? 'border-purple-500 bg-purple-50'
                : 'border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => onSelectSection('aiSettings')}
          >
            <div className="flex items-center gap-3">
              <SparklesIcon className="h-5 w-5 text-purple-500" />
              <div>
                <Typography variant="small" className="font-medium">
                  AI Settings
                </Typography>
                <Typography variant="small" color="gray" className="text-xs">
                  Prompt & guidelines
                </Typography>
              </div>
            </div>
            {hasGuidelinesChanges && (
              <Chip
                size="sm"
                value="Modified"
                color="amber"
                variant="ghost"
              />
            )}
          </div>

          {/* AI Costs */}
          <div
            className={`flex items-center p-3 rounded-lg border transition-colors cursor-pointer ${
              isAiCostsSelected
                ? 'border-purple-500 bg-purple-50'
                : 'border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => onSelectSection('aiCosts')}
          >
            <div className="flex items-center gap-3">
              <CurrencyDollarIcon className="h-5 w-5 text-purple-500" />
              <div>
                <Typography variant="small" className="font-medium">
                  AI Costs
                </Typography>
                <Typography variant="small" color="gray" className="text-xs">
                  Generation cost breakdown
                </Typography>
              </div>
            </div>
          </div>

          {/* Listings */}
          <div
            className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
              isListingsSelected
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:bg-gray-50'
            }`}
            onClick={() => onSelectSection('listings')}
          >
            <div className="flex items-center gap-3">
              <QueueListIcon className="h-5 w-5 text-blue-500" />
              <div>
                <Typography variant="small" className="font-medium">
                  {listingsTitle}
                </Typography>
                <Typography variant="small" color="gray" className="text-xs">
                  Source data & exclusions
                </Typography>
              </div>
            </div>
            {hasExclusionChanges && (
              <Chip
                size="sm"
                value={pendingExclusionsCount > 0 ? `${pendingExclusionsCount} pending` : 'Pending'}
                color="amber"
                variant="ghost"
              />
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-1" />

          {/* Report sections */}
          {sortSections(sections).map((section) => (
            <div
              key={section.id}
              className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
                selectedSection === section.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
              onClick={() => onSelectSection(section.id)}
            >
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={section.included}
                  onChange={() => onToggleSection(section.id, !section.included)}
                  containerProps={{ className: '-ml-2' }}
                  onClick={(e) => e.stopPropagation()}
                />
                <Typography variant="small" className="font-medium">
                  {SECTION_LABELS[section.id] || section.id}
                </Typography>
              </div>
              {section.id === 'recentlySoldComps' && hasSalesCompExclusionChanges && (
                <Chip
                  size="sm"
                  value="Modified"
                  color="amber"
                  variant="ghost"
                />
              )}
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

export default SectionList;
